import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'outline' | 'ghost' | 'dark';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary-hover shadow-cta disabled:opacity-60',
  outline:
    'border-[1.5px] border-primary text-primary hover:bg-primary-light bg-transparent',
  ghost: 'text-content-secondary hover:bg-surface-card-hover bg-transparent',
  dark: 'bg-content-primary text-surface-card hover:opacity-90'
};

const sizes: Record<Size, string> = {
  sm: 'text-xs px-3 py-2 rounded-md',
  md: 'text-sm px-4 py-2.5 rounded-lg',
  lg: 'text-[15px] px-5 py-3 rounded-xl'
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, className, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-display font-bold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variants[variant],
        sizes[size],
        block && 'w-full',
        className
      )}
      {...props}
    />
  );
});
